import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// ─── Utilitaire d'export ─────────────────────────────────────────────────────
const exportData = (rows, filename, format) => {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Export");

  if (format === "csv") {
    const csv = XLSX.utils.sheet_to_csv(ws, { FS: ";" });
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    saveAs(blob, `${filename}_${new Date().toISOString().slice(0, 10)}.csv`);
  } else {
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `${filename}_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  }
};
// ─────────────────────────────────────────────────────────────────────────────

function Organisations() {
  const [organisations, setOrganisations] = useState([]);
  const [filteredOrganisations, setFilteredOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [organisationDetails, setOrganisationDetails] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    title: "",
    message: "",
    onConfirm: null,
    type: "warning",
  });
  const organisationsPerPage = 15;

  const navigate = useNavigate();

  // Fonction pour afficher les messages
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 3000);
  };

  // Fonction pour transformer les données de l'API (snake_case → camelCase)
  const transformOrganisation = (org) => {
    return {
      ...org,
      organisationId: org.organisation_id,
      userId: org.user_id,
      parentId: org.parent_id,
      accountType: org.account_type,
      createdAt: org.created_at,
      updatedAt: org.updated_at,
      organisationType: org.organisation_type,
    };
  };

  // Récupération des organisations depuis l'API
  useEffect(() => {
    const fetchAllOrganisations = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/organisations`,
        );
        if (!res.ok)
          throw new Error("Erreur lors de la récupération des organisations");
        const mainOrgs = await res.json();

        // ✅ Transformer les données en camelCase
        let allOrgs = mainOrgs.map(transformOrganisation);

        // Récupérer les sub-organisations pour chaque organisation principale (en parallèle)
        const subOrgsResponses = await Promise.all(
          mainOrgs.map((main) =>
            fetch(
              `${process.env.REACT_APP_API_URL}/api/organisations/${main.organisation_id}/sub-organisations`,
            )
              .then((res) => (res.ok ? res.json() : []))
              .catch(() => {
                console.warn(
                  `Impossible de charger les sous-organisations pour ${main.organisation_id}`,
                );
                return [];
              }),
          ),
        );

        // ✅ Transformer les sub-organisations aussi
        const subOrgs = subOrgsResponses.flat().map(transformOrganisation);
        allOrgs = [...allOrgs, ...subOrgs];

        setOrganisations(allOrgs);
        setFilteredOrganisations(allOrgs);
      } catch (error) {
        console.error("Erreur lors du chargement :", error);
        showMessage("Erreur lors du chargement des données", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchAllOrganisations();
  }, []);

  // Fonction pour récupérer les détails d'une organisation
  const fetchOrganisationDetails = async (organisationId) => {
    try {
      setDetailsLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organisations/${organisationId}`,
      );

      if (!res.ok) {
        throw new Error("Organisation introuvable");
      }

      const data = await res.json();
      setOrganisationDetails(data);
      setModalVisible(true);
    } catch (error) {
      console.error("Erreur :", error.message);
      showMessage("Erreur lors du chargement des détails", "error");
      setOrganisationDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Fonction pour changer le statut d'une organisation
  const toggleStatus = async (org) => {
    setActionLoading(true);
    setOpenDropdownId(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organisations/${org.organisationId}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: org.status === "active" ? "inactive" : "active",
          }),
        },
      );

      if (!res.ok) {
        throw new Error("Erreur lors du changement de statut");
      }

      showMessage(
        `Organisation ${
          org.status === "active" ? "désactivée" : "activée"
        } avec succès`,
        "success",
      );

      // Rafraîchir les données
      const updatedOrgs = organisations.map((o) =>
        o.organisationId === org.organisationId
          ? { ...o, status: org.status === "active" ? "inactive" : "active" }
          : o,
      );
      setOrganisations(updatedOrgs);
      setFilteredOrganisations(updatedOrgs);
    } catch (err) {
      showMessage(err.message, "error");
    } finally {
      setActionLoading(false);
    }
  };

  // Fonction pour afficher la modal de confirmation
  const showConfirmModal = (title, message, onConfirm, type = "warning") => {
    setConfirmModal({
      visible: true,
      title,
      message,
      onConfirm,
      type,
    });
  };

  // Fonction pour fermer la modal de confirmation
  const closeConfirmModal = () => {
    setConfirmModal({
      visible: false,
      title: "",
      message: "",
      onConfirm: null,
      type: "warning",
    });
  };

  // Fonction pour gérer le statut du wallet
  const toggleWalletStatus = async (org) => {
    const currentWalletStatus = org.wallet?.statut || "active";
    const newStatus = currentWalletStatus === "active" ? "inactive" : "active";

    showConfirmModal(
      newStatus === "inactive" ? "Désactiver le wallet" : "Réactiver le wallet",
      `Êtes-vous sûr de vouloir ${
        newStatus === "inactive" ? "désactiver" : "réactiver"
      } le wallet de l'organisation "${org.name}" ?`,
      async () => {
        await executeWalletStatusChange(org, newStatus);
      },
      newStatus === "inactive" ? "warning" : "success",
    );
  };

  // Fonction pour exécuter le changement de statut du wallet
  const executeWalletStatusChange = async (org, newStatus) => {
    setActionLoading(true);
    setOpenDropdownId(null);
    closeConfirmModal();

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organisations/${org.organisationId}/reset-wallet`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            statut: newStatus,
          }),
        },
      );

      if (!res.ok) {
        throw new Error("Erreur lors du changement de statut du wallet");
      }

      const data = await res.json();

      showMessage(
        `Wallet ${
          newStatus === "inactive" ? "désactivé" : "réactivé"
        } avec succès`,
        "success",
      );

      // Rafraîchir les données
      const updatedOrgs = organisations.map((o) =>
        o.organisationId === org.organisationId
          ? {
              ...o,
              wallet: data.wallet,
            }
          : o,
      );
      setOrganisations(updatedOrgs);
      setFilteredOrganisations(updatedOrgs);

      // Rafraîchir les détails si la modal est ouverte pour cette organisation
      if (organisationDetails?.organisationId === org.organisationId) {
        await fetchOrganisationDetails(org.organisationId);
      }
    } catch (err) {
      showMessage(err.message, "error");
    } finally {
      setActionLoading(false);
    }
  };

  // Fonction pour supprimer une organisation
  const deleteOrganisation = async (org) => {
    showConfirmModal(
      "Supprimer l'organisation",
      `Êtes-vous sûr de vouloir supprimer l'organisation "${org.name}" ? Cette action est irréversible.`,
      async () => {
        await executeDeleteOrganisation(org);
      },
      "danger",
    );
  };

  // Fonction pour exécuter la suppression
  const executeDeleteOrganisation = async (org) => {
    setActionLoading(true);
    setOpenDropdownId(null);
    closeConfirmModal();

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organisations/${org.organisationId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!res.ok) {
        throw new Error("Erreur lors de la suppression");
      }

      showMessage(`Organisation ${org.name} supprimée avec succès`, "success");

      // Rafraîchir les données
      const updatedOrgs = organisations.filter(
        (o) => o.organisationId !== org.organisationId,
      );
      setOrganisations(updatedOrgs);
      setFilteredOrganisations(updatedOrgs);
    } catch (err) {
      showMessage(err.message, "error");
    } finally {
      setActionLoading(false);
    }
  };

  // ✅ CORRECTION: Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    if (!openDropdownId) return;

    const handleClickOutside = (event) => {
      // Chercher le conteneur du dropdown le plus proche
      const dropdownContainer = event.target.closest(
        "[data-dropdown-container]",
      );

      // Si le clic est en dehors de tous les dropdowns, fermer
      if (!dropdownContainer) {
        setOpenDropdownId(null);
      }
    };

    // Utiliser setTimeout pour éviter que le clic d'ouverture ferme immédiatement le dropdown
    const timeoutId = setTimeout(() => {
      document.addEventListener("click", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [openDropdownId]);

  // Filtrage des organisations
  useEffect(() => {
    let filtered = organisations;

    if (searchTerm) {
      filtered = filtered.filter((org) => {
        const searchableText = `${org.name} ${org.owner?.fullname || ""} ${
          org.owner?.phone || ""
        } ${org.phone || ""}`;
        return searchableText.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((org) =>
        ["main", "sub"].includes(statusFilter)
          ? org.organisationType === statusFilter
          : org.accountType === statusFilter,
      );
    }

    setFilteredOrganisations(filtered);
    setCurrentPage(1);
  }, [organisations, searchTerm, statusFilter]);

  // Pagination
  const indexOfLastOrg = currentPage * organisationsPerPage;
  const indexOfFirstOrg = indexOfLastOrg - organisationsPerPage;
  const currentOrganisations = filteredOrganisations.slice(
    indexOfFirstOrg,
    indexOfLastOrg,
  );
  const totalPages = Math.ceil(
    filteredOrganisations.length / organisationsPerPage,
  );

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handleExport = (format) => {
    const rows = filteredOrganisations.map((org) => ({
      "ID Organisation": org.organisationId || "",
      Nom: org.name || "",
      Téléphone: org.phone ? `+${org.phone}` : "",
      Statut: org.status || "",
      Type: org.organisationType === "main" ? "Principale" : "Secondaire",
      "Type de compte": org.accountType || "",
      Propriétaire: org.owner?.fullname || "",
      "Tél. propriétaire": org.owner?.phone ? `+${org.owner.phone}` : "",
      "Solde wallet": org.wallet?.balance ?? "",
      "Statut wallet": org.wallet?.statut || "",
      "Date de création": org.createdAt
        ? new Date(org.createdAt).toUTCString()
        : "",
    }));
    exportData(rows, "organisations", format);
  };

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      {/* Message d'alerte */}
      {message.text && (
        <div
          style={{
            padding: "10px",
            marginBottom: "20px",
            borderRadius: "4px",
            backgroundColor: message.type === "success" ? "#f6ffed" : "#fff2f0",
            color: message.type === "success" ? "#52c41a" : "#ff4d4f",
            border: `1px solid ${
              message.type === "success" ? "#b7eb8f" : "#ffccc7"
            }`,
          }}
        >
          {message.text}
        </div>
      )}

      {/* En-tête avec filtres */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <h1 style={{ fontSize: "24px", fontWeight: "bold", margin: 0 }}>
          Organisations
        </h1>

        <div style={{ display: "flex", gap: "10px" }}>
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #d9d9d9",
              borderRadius: "4px",
              fontSize: "14px",
              minWidth: "200px",
            }}
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #d9d9d9",
              borderRadius: "4px",
              fontSize: "14px",
            }}
          >
            <option value="all">Tous</option>
            <option value="main">Principale</option>
            <option value="sub">Secondaire</option>
            <option value="marchand">Marchand</option>
            <option value="school">Ecole</option>
            <option value="enterprise">Entreprise</option>
          </select>

          {/* Bouton Exporter */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() =>
                setOpenDropdownId(
                  openDropdownId === "__export__" ? null : "__export__",
                )
              }
              style={{
                padding: "8px 16px",
                border: "1px solid #1890ff",
                backgroundColor: "#1890ff",
                color: "white",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              ⬇ Exporter
            </button>
            {openDropdownId === "__export__" && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  right: 0,
                  backgroundColor: "white",
                  border: "1px solid #d9d9d9",
                  borderRadius: "6px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  zIndex: 1000,
                  minWidth: "180px",
                  overflow: "hidden",
                }}
              >
                {[
                  { label: "📄 Exporter en CSV", format: "csv" },
                  { label: "📊 Exporter en Excel (.xlsx)", format: "xlsx" },
                ].map(({ label, format }) => (
                  <button
                    key={format}
                    onClick={() => {
                      handleExport(format);
                      setOpenDropdownId(null);
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "10px 16px",
                      border: "none",
                      backgroundColor: "transparent",
                      textAlign: "left",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f5f5f5")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tableau des organisations */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                backgroundColor: "white",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#fafafa" }}>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: "600",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Nom
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: "600",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Propriétaire
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: "600",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Téléphone
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: "600",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Type
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: "600",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Compte
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      fontWeight: "600",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Statut
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      fontWeight: "600",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Wallet
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      fontWeight: "600",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentOrganisations.map((org, index) => (
                  <tr
                    key={org.organisationId}
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      backgroundColor: index % 2 === 0 ? "white" : "#fafafa",
                    }}
                  >
                    <td style={{ padding: "12px" }}>{org.name}</td>
                    <td style={{ padding: "12px" }}>
                      {org.owner?.fullname || "N/A"}
                    </td>
                    <td style={{ padding: "12px" }}>+{org.phone || "N/A"}</td>
                    <td style={{ padding: "12px" }}>
                      {org.organisationType === "main"
                        ? "Principale"
                        : "Secondaire"}
                    </td>
                    <td style={{ padding: "12px" }}>
                      {org.accountType || "N/A"}
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "500",
                          backgroundColor:
                            org.status === "active" ? "#f6ffed" : "#fff2f0",
                          color:
                            org.status === "active" ? "#52c41a" : "#ff4d4f",
                        }}
                      >
                        {org.status === "active" ? "Actif" : "Inactif"}
                      </span>
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      {org.wallet ? (
                        <span
                          style={{
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "500",
                            backgroundColor:
                              org.wallet.statut === "active"
                                ? "#e6f7ff"
                                : "#fff1f0",
                            color:
                              org.wallet.statut === "active"
                                ? "#1890ff"
                                : "#ff4d4f",
                          }}
                        >
                          {org.wallet.statut === "active" ? "Actif" : "Inactif"}
                        </span>
                      ) : (
                        <span style={{ color: "#999", fontSize: "12px" }}>
                          N/A
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      {/* ✅ CORRECTION: Ajout de data-dropdown-container */}
                      <div
                        data-dropdown-container
                        style={{
                          position: "relative",
                          display: "inline-block",
                        }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // ✅ CORRECTION: Vérifier que l'ID existe avant de toggle
                            if (org?.organisationId) {
                              setOpenDropdownId(
                                openDropdownId === org.organisationId
                                  ? null
                                  : org.organisationId,
                              );
                            }
                          }}
                          disabled={actionLoading}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #d9d9d9",
                            backgroundColor: "white",
                            borderRadius: "4px",
                            cursor: actionLoading ? "not-allowed" : "pointer",
                            fontSize: "14px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            opacity: actionLoading ? 0.6 : 1,
                          }}
                        >
                          Actions
                          <span style={{ fontSize: "10px" }}>▼</span>
                        </button>

                        {/* ✅ CORRECTION: Triple vérification avant d'afficher le dropdown */}
                        {openDropdownId &&
                          org?.organisationId &&
                          openDropdownId === org.organisationId && (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                position: "absolute",
                                top: "100%",
                                right: 0,
                                marginTop: "4px",
                                backgroundColor: "white",
                                border: "1px solid #d9d9d9",
                                borderRadius: "4px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                minWidth: "200px",
                                zIndex: 1000,
                              }}
                            >
                              <button
                                onClick={() => {
                                  navigate(
                                    `/organisations/${org.organisationId}`,
                                  );
                                  setOpenDropdownId(null);
                                }}
                                style={{
                                  width: "100%",
                                  padding: "10px 16px",
                                  border: "none",
                                  backgroundColor: "transparent",
                                  textAlign: "left",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  borderBottom: "1px solid #f0f0f0",
                                }}
                                onMouseEnter={(e) =>
                                  (e.target.style.backgroundColor = "#f5f5f5")
                                }
                                onMouseLeave={(e) =>
                                  (e.target.style.backgroundColor =
                                    "transparent")
                                }
                              >
                                👁️ Voir les détails
                              </button>

                              <button
                                onClick={() => toggleStatus(org)}
                                style={{
                                  width: "100%",
                                  padding: "10px 16px",
                                  border: "none",
                                  backgroundColor: "transparent",
                                  textAlign: "left",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  borderBottom: "1px solid #f0f0f0",
                                  color:
                                    org.status === "active"
                                      ? "#ff4d4f"
                                      : "#52c41a",
                                }}
                                onMouseEnter={(e) =>
                                  (e.target.style.backgroundColor = "#f5f5f5")
                                }
                                onMouseLeave={(e) =>
                                  (e.target.style.backgroundColor =
                                    "transparent")
                                }
                              >
                                {org.status === "active"
                                  ? "❌ Désactiver l'organisation"
                                  : "✅ Activer l'organisation"}
                              </button>

                              {org.wallet && (
                                <button
                                  onClick={() => toggleWalletStatus(org)}
                                  style={{
                                    width: "100%",
                                    padding: "10px 16px",
                                    border: "none",
                                    backgroundColor: "transparent",
                                    textAlign: "left",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    borderBottom: "1px solid #f0f0f0",
                                    color:
                                      org.wallet.statut === "active"
                                        ? "#ff4d4f"
                                        : "#1890ff",
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.target.style.backgroundColor = "#f5f5f5")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.target.style.backgroundColor =
                                      "transparent")
                                  }
                                >
                                  {org.wallet.statut === "active"
                                    ? "💳 Désactiver le wallet"
                                    : "💳 Réactiver le wallet"}
                                </button>
                              )}

                              <button
                                onClick={() => deleteOrganisation(org)}
                                style={{
                                  width: "100%",
                                  padding: "10px 16px",
                                  border: "none",
                                  backgroundColor: "transparent",
                                  textAlign: "left",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  color: "#ff4d4f",
                                }}
                                onMouseEnter={(e) =>
                                  (e.target.style.backgroundColor = "#fff2f0")
                                }
                                onMouseLeave={(e) =>
                                  (e.target.style.backgroundColor =
                                    "transparent")
                                }
                              >
                                🗑️ Supprimer
                              </button>
                            </div>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "20px",
              padding: "10px",
            }}
          >
            <div style={{ fontSize: "14px", color: "#666" }}>
              Affichage de {indexOfFirstOrg + 1} à{" "}
              {Math.min(indexOfLastOrg, filteredOrganisations.length)} sur{" "}
              {filteredOrganisations.length} organisations
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                style={{
                  padding: "6px 12px",
                  border: "1px solid #d9d9d9",
                  backgroundColor: currentPage === 1 ? "#f5f5f5" : "white",
                  borderRadius: "4px",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                }}
              >
                Précédent
              </button>
              <span style={{ padding: "6px 12px", fontSize: "14px" }}>
                Page {currentPage} sur {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                style={{
                  padding: "6px 12px",
                  border: "1px solid #d9d9d9",
                  backgroundColor:
                    currentPage === totalPages ? "#f5f5f5" : "white",
                  borderRadius: "4px",
                  cursor:
                    currentPage === totalPages ? "not-allowed" : "pointer",
                }}
              >
                Suivant
              </button>
            </div>
          </div>
        </>
      )}

      {/* Modal de confirmation stylée */}
      {confirmModal.visible && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
            animation: "fadeIn 0.2s ease-in-out",
          }}
          onClick={closeConfirmModal}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "0",
              maxWidth: "450px",
              width: "90%",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
              animation: "slideDown 0.3s ease-out",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* En-tête coloré selon le type */}
            <div
              style={{
                padding: "24px",
                backgroundColor:
                  confirmModal.type === "danger"
                    ? "#ff4d4f"
                    : confirmModal.type === "success"
                      ? "#52c41a"
                      : "#faad14",
                color: "white",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <span style={{ fontSize: "28px" }}>
                {confirmModal.type === "danger"
                  ? "⚠️"
                  : confirmModal.type === "success"
                    ? "✅"
                    : "❗"}
              </span>
              <h3 style={{ margin: 0, fontSize: "20px", fontWeight: "600" }}>
                {confirmModal.title}
              </h3>
            </div>

            {/* Contenu */}
            <div style={{ padding: "24px" }}>
              <p
                style={{
                  margin: 0,
                  fontSize: "15px",
                  lineHeight: "1.6",
                  color: "#333",
                }}
              >
                {confirmModal.message}
              </p>
            </div>

            {/* Boutons d'action */}
            <div
              style={{
                padding: "16px 24px",
                backgroundColor: "#fafafa",
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <button
                onClick={closeConfirmModal}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #d9d9d9",
                  backgroundColor: "white",
                  color: "#333",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#f5f5f5";
                  e.target.style.borderColor = "#b3b3b3";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "white";
                  e.target.style.borderColor = "#d9d9d9";
                }}
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  if (confirmModal.onConfirm) {
                    confirmModal.onConfirm();
                  }
                }}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  backgroundColor:
                    confirmModal.type === "danger"
                      ? "#ff4d4f"
                      : confirmModal.type === "success"
                        ? "#52c41a"
                        : "#faad14",
                  color: "white",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = "0.85";
                  e.target.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = "1";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styles CSS pour les animations */}
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes slideDown {
            from {
              transform: translateY(-20px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
}

export default Organisations;
